import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useAlertsStore } from "./AlertsStore/alertsStore";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { IconButton, Paper, TableBody, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getUserTimezone } from "../../utils/getUserTimezone";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

// Utility function to format timestamp with timezone
export const formatTimestampWithTimezone = (
  timestamp: number,
  timezone: string
): string => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    timeZone: timezone,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// "muted 12 min ago by ops@example.com" — how long ago a mute started.
export const formatMutedAgo = (startMs: number, nowMs: number): string => {
  const minutes = Math.max(0, Math.floor((nowMs - startMs) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours} hr ${rest} min ago` : `${hours} hr ago`;
};

const mutedByLabel = (mutedBy: string | null | undefined): string =>
  mutedBy ? mutedBy : "—";

// Lists the shared, fleet-wide mutes (ENG-2103): every operator sees every
// mute on the sites they can view, whoever set it.
export const SeeMutedSiteAndCamerasDialog = () => {
  const {
    seeMutedSiteAndCamerasDialogOpen,
    setSeeMutedSiteAndCamerasDialogOpen,
    mutedAlertSites,
    mutedAlertCameras,
    fetchSharedMutes,
    unmuteSharedMute,
  } = useAlertsStore();

  const navigate = useNavigate();

  // Row key of the unmute in flight, so it can't be double-fired.
  const [unmutingKey, setUnmutingKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (seeMutedSiteAndCamerasDialogOpen) {
      fetchSharedMutes(navigate);
    }
    // Fetch once per open; navigate's identity is irrelevant here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeMutedSiteAndCamerasDialogOpen, fetchSharedMutes]);

  const handleUnmute = async (
    key: string,
    scope: "site" | "camera",
    entry: { site_id?: number; camera_id?: number }
  ) => {
    setUnmutingKey(key);
    try {
      await unmuteSharedMute(scope, entry, navigate);
    } finally {
      setUnmutingKey(null);
    }
  };

  // Get the current user's timezone
  const userTimezone = getUserTimezone();
  const now = Date.now();
  return (
    <Dialog
      open={seeMutedSiteAndCamerasDialogOpen}
      fullWidth={
        mutedAlertSites.length > 0 || mutedAlertCameras.length > 0
          ? true
          : false
      }
      maxWidth={
        mutedAlertSites.length > 0 || mutedAlertCameras.length > 0 ? "md" : "xs"
      }
      onClose={() => setSeeMutedSiteAndCamerasDialogOpen(false)}
    >
      <IconButton
        aria-label="close"
        onClick={() => {
          setSeeMutedSiteAndCamerasDialogOpen(false);
        }}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ minHeight: 200, maxHeight: 600 }}>
        <Typography
          sx={{ fontFamily: "Mulish", fontSize: 18, fontWeight: 500 }}
        >
          Muted Sites
        </Typography>
        {mutedAlertSites.length > 0 ? (
          <div className="pt-[20px]">
            <Paper>
              <TableContainer>
                <Table sx={{ width: "100%" }}>
                  <colgroup>
                    <col style={{ width: "31.67%" }} />
                    <col style={{ width: "31.67%" }} />
                    <col style={{ width: "31.67%" }} />
                    <col style={{ width: "5%", minWidth: "50px" }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell>Site Name</TableCell>
                      <TableCell>Muted Until</TableCell>
                      <TableCell>Muted By</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mutedAlertSites.map((site) => (
                      <TableRow key={`site-${site.site_id}`}>
                        <TableCell>{site.site_name}</TableCell>
                        <TableCell>
                          until{" "}
                          {formatTimestampWithTimezone(
                            site.mute_end_time,
                            userTimezone
                          )}
                          &nbsp;
                          <Tooltip
                            title={`${userTimezone}`}
                            placement="top"
                            sx={{ fontSize: "10px", fontFamily: "mulish" }}
                          >
                            <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          muted {formatMutedAgo(site.mute_start_time, now)} by{" "}
                          {mutedByLabel(site.muted_by)}
                        </TableCell>
                        <TableCell>
                          <OrangeTextButton
                            onClick={() =>
                              handleUnmute(`site-${site.site_id}`, "site", site)
                            }
                            disabled={unmutingKey === `site-${site.site_id}`}
                            text={
                              <Tooltip title="Unmute Site" placement="top">
                                <DeleteIcon />
                              </Tooltip>
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </div>
        ) : (
          <div className="pt-[5px]">
            <Typography
              sx={{ fontFamily: "Mulish", fontSize: 16, fontWeight: 500 }}
            >
              No muted sites
            </Typography>
          </div>
        )}
        <div className="pt-[20px]"></div>
        <div className="pt-[20px]">
          <Typography
            sx={{ fontFamily: "Mulish", fontSize: 18, fontWeight: 500 }}
          >
            Muted Cameras
          </Typography>
          {mutedAlertCameras.length > 0 ? (
            <div className="pt-[20px]">
              <Paper>
                <TableContainer>
                  <Table sx={{ width: "100%" }}>
                    <colgroup>
                      <col style={{ width: "23.75%" }} />
                      <col style={{ width: "23.75%" }} />
                      <col style={{ width: "23.75%" }} />
                      <col style={{ width: "23.75%" }} />
                      <col style={{ width: "5%", minWidth: "50px" }} />
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        <TableCell>Camera Name</TableCell>
                        <TableCell>Site Name</TableCell>
                        <TableCell>Muted Until</TableCell>
                        <TableCell>Muted By</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mutedAlertCameras.map((camera) => (
                        <TableRow key={`camera-${camera.camera_id}`}>
                          <TableCell>{camera.camera_name} </TableCell>
                          <TableCell>{camera.site_name}</TableCell>
                          <TableCell>
                            until{" "}
                            {formatTimestampWithTimezone(
                              camera.mute_end_time,
                              userTimezone
                            )}
                            &nbsp;
                            <Tooltip
                              title={`${userTimezone}`}
                              placement="top"
                              sx={{ fontSize: "10px", fontFamily: "mulish" }}
                            >
                              <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            muted {formatMutedAgo(camera.mute_start_time, now)}{" "}
                            by {mutedByLabel(camera.muted_by)}
                          </TableCell>
                          <TableCell>
                            <OrangeTextButton
                              onClick={() =>
                                // scope="site" rows are the cameras left over
                                // from a split site mute: unmute per camera.
                                handleUnmute(
                                  `camera-${camera.camera_id}`,
                                  "camera",
                                  camera
                                )
                              }
                              text={
                                <Tooltip title="Unmute Camera" placement="top">
                                  <DeleteIcon />
                                </Tooltip>
                              }
                              disabled={
                                unmutingKey === `camera-${camera.camera_id}`
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </div>
          ) : (
            <div className="pt-[5px]">
              <Typography
                sx={{ fontFamily: "Mulish", fontSize: 16, fontWeight: 500 }}
              >
                No muted cameras
              </Typography>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
